// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the Organizer's door
//  ---------------------------------------------------------------------------
//  NOT md-organizer.ts, which is its sibling and not its parent. That file is
//  the proposal authority: WHAT the Organizer puts forward for a cell, and how a
//  proposal is reviewed, guarded and committed. This file is the door: WHO is
//  writing at this instant, and what is off-limits to them. They share a prefix
//  and nothing else — no call, no document, no state — and they were written by
//  two separate phases that never saw each other. Keeping them apart is
//  deliberate: authority is a lifecycle with documents behind it, attribution is
//  one word that every write path must be unable to forget.
//
//  The AI Organizer is not built yet. This is the door it will come through,
//  written first on purpose: a door fitted after the thing that walks through it
//  is a door shaped by whatever that thing found convenient.
//
//  IT IS A DOOR, NOT A ROUTE. Every function here delegates to the same
//  `mdDatabaseTry*` surface a person's keystroke reaches, which reaches the
//  storage chokepoint, which is where the rest of the app already lives. There
//  is deliberately no faster path for the agent — a second write route would be
//  a second set of rules to keep in step, and the one that drifts is always the
//  one nobody is looking at.
//
//  WHAT THE DOOR ADDS is three things the surface behind it cannot know:
//
//    WHO. `mdOrganizerActor()` is 'user' until something wraps a call in
//    `mdOrganizerAs`, and 'user' again the moment that call returns — including
//    when it returns by throwing. md-membership.ts asks, and stamps `by` on the
//    edge it writes. Nothing has to remember to pass an argument, which is what
//    keeps an unattributed write from being one refactor away.
//
//    LOCKED MEANS LOCKED. `mdRecordLocked` already answers for the whole app;
//    here it is absolute. IQAMA can be read and never pruned, never renamed,
//    never unfiled, never dropped from a database it sits in. The refusal is the
//    same `locked` word every other surface already handles, and it lives HERE
//    rather than in md-database.ts because a person may still do all of those
//    things — nothing about the app the user touches changes.
//
//    AND THE LADDER STAYS THE USER'S. md-project.ts refuses a status change
//    outright while the actor is the agent. A status is somebody's own word
//    about where they stand; a machine that can set one is a machine that can
//    promote a person into a commitment they never made.
//
//  Headless by contract, like md-librarian.ts: no DOM, no React, no store
//  singleton. The Organizer will run wherever it runs, and a door that needs a
//  rendered page to open is not a door.
// ════════════════════════════════════════════════════════════════════════════

type MdOrganizerActor = 'user' | 'agent';

interface MdOrganizerResult {
  ok: boolean;
  reason?: string;
  value?: unknown;
  changed?: boolean;
}

(function (g: any) {
  'use strict';

  const USER: MdOrganizerActor = 'user';
  const AGENT: MdOrganizerActor = 'agent';

  /** Who is writing right now. Module state rather than a threaded argument,
      because an argument is a thing a call site can forget and this is a thing
      no call site may forget. */
  let actor: MdOrganizerActor = USER;

  function mdOrganizerActor(): MdOrganizerActor { return actor; }

  /** Run one synchronous call as the agent. `finally` is the whole point: an
      Organizer that throws mid-sweep must not leave the app writing as the
      agent for every keystroke after it. Nesting does not stack — an agent
      inside an agent is still one agent — and the actor is restored to what it
      was rather than to 'user', so this composes with itself. */
  function mdOrganizerAs<T>(fn: () => T): T {
    if (typeof fn !== 'function') return undefined as unknown as T;
    const before = actor;
    actor = AGENT;
    try { return fn(); } finally { actor = before; }
  }

  const locked = (): MdOrganizerResult => ({ ok: false, reason: 'locked' });
  const missing = (): MdOrganizerResult => ({ ok: false, reason: 'missing' });

  /** The record a row names. An ordinary member row is named by its edge, a Main
      DB row by the ref key itself, and a native row by neither — which reads as
      a key nothing is locked under, so the guard below simply passes it on. */
  function keyFor(dbId: unknown, rowId: unknown): string {
    const entry = typeof g.mdMembershipEntry === 'function' ? g.mdMembershipEntry(dbId, rowId) : null;
    if (entry && typeof entry.k === 'string') return entry.k;
    return typeof rowId === 'string' ? rowId : '';
  }

  const isLocked = (key: string): boolean =>
    !!key && typeof g.mdRecordLocked === 'function' && !!g.mdRecordLocked(key);

  /** Fill a cell. The two-kinds-of-cell line is held one layer down, exactly as
      it is for a person: a database-owned cell becomes an edge, a record-owned
      cell is written to the record's own home, and either way one
      `mdDatabaseUndo` takes it back. */
  function mdOrganizerCellSet(dbId: unknown, rowId: unknown, propertyId: unknown, value: unknown): MdOrganizerResult {
    if (isLocked(keyFor(dbId, rowId))) return locked();
    if (typeof g.mdDatabaseTryCellSet !== 'function') return missing();
    return mdOrganizerAs(() => g.mdDatabaseTryCellSet(dbId, rowId, propertyId, value));
  }

  /** File a record into a database. */
  function mdOrganizerMemberAdd(dbId: unknown, refKey: unknown): MdOrganizerResult {
    if (isLocked(typeof refKey === 'string' ? refKey : '')) return locked();
    if (typeof g.mdDatabaseTryMemberAdd !== 'function') return missing();
    return mdOrganizerAs(() => g.mdDatabaseTryMemberAdd(dbId, refKey));
  }

  /** Prune a record out of a database — its MEMBERSHIP, never the record, which
      keeps existing wherever it lives. The agent has no way to delete anything
      through this door, and that is not an oversight. */
  function mdOrganizerMemberRemove(dbId: unknown, rowId: unknown): MdOrganizerResult {
    if (isLocked(keyFor(dbId, rowId))) return locked();
    if (typeof g.mdDatabaseTryRowsRemove !== 'function') return missing();
    return mdOrganizerAs(() => g.mdDatabaseTryRowsRemove(dbId, [rowId]));
  }

  Object.assign(g, {
    mdOrganizerActor, mdOrganizerAs,
    mdOrganizerCellSet, mdOrganizerMemberAdd, mdOrganizerMemberRemove,
  });

  // CommonJS escape hatch, following md-record.ts and md-librarian.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdOrganizerActor, mdOrganizerAs,
      mdOrganizerCellSet, mdOrganizerMemberAdd, mdOrganizerMemberRemove };
  }
})(typeof window !== 'undefined' ? window : globalThis);
